import { useState } from 'react';
import Banner from '@savvy-kit/patterns/banner';
import { isNil } from 'es-toolkit';
import { AggregatorConnectButton } from 'src/components/aggregator/aggregator-connect-button';

import type { StaleConnection, StaleConnectionReason } from './types';
import { CLIENT_PORTAL_BANNER_TITLE_WRAP_CLASS } from '../common/constants';
import {
  STALENESS_RECONNECT_LABEL,
  STALENESS_RECONNECT_UNSUPPORTED_NOTE,
  stalenessDelayedDescription,
  stalenessDelayedTitle,
  stalenessDisconnectedDescription,
  stalenessDisconnectedTitle,
  stalenessReconnectLabel,
} from './constants';
import { institutionLabel, institutionListLabel, lastSyncedLabel } from './utils';

function oldestSyncLabel(connections: readonly StaleConnection[]) {
  const stamps = connections.flatMap((connection) => (isNil(connection.lastSyncedAt) ? [] : [connection.lastSyncedAt]));
  if (stamps.length === 0) {
    return null;
  }
  return lastSyncedLabel([...stamps].sort().at(0) ?? null);
}

type ReconnectButtonProps = {
  readonly connection: StaleConnection;
  readonly label: string;
  readonly onReconnected: () => Promise<unknown>;
};

function ReconnectButton({ connection, label, onReconnected }: ReconnectButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <AggregatorConnectButton
      title={label}
      providerAccountId={connection.id}
      providerAccountSource={connection.source}
      setIsLoading={setIsLoading}
      buttonProps={{ variant: 'secondary', size: 'sm', disabled: isLoading, className: 'shrink-0 whitespace-nowrap' }}
      onSuccess={() => onReconnected()}
    />
  );
}

type DisconnectedBannerProps = {
  readonly connections: readonly StaleConnection[];
  readonly onDismiss: () => void;
  readonly onReconnected: () => Promise<unknown>;
};

function DisconnectedBanner({ connections, onDismiss, onReconnected }: DisconnectedBannerProps) {
  const reconnectable = connections.filter((connection) => connection.reconnectSupported);
  const single = connections.length === 1;
  const requiresSignIn = connections.every((connection) => connection.requiresSignIn);
  const inlineButtonConnections = single ? [] : reconnectable;
  const headerButtonConnection = single ? reconnectable.at(0) : undefined;

  return (
    <Banner
      variant="warning"
      className={CLIENT_PORTAL_BANNER_TITLE_WRAP_CLASS}
      data-testid="accounts-staleness-disconnected-banner"
      title={stalenessDisconnectedTitle(connections.map(institutionLabel), requiresSignIn)}
      description={
        <div className="flex flex-col gap-2">
          <span>
            {stalenessDisconnectedDescription(
              institutionListLabel(connections),
              oldestSyncLabel(connections),
              requiresSignIn,
            )}
          </span>
          {reconnectable.length === 0 && <span>{STALENESS_RECONNECT_UNSUPPORTED_NOTE}</span>}
          {inlineButtonConnections.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {inlineButtonConnections.map((connection) => (
                <ReconnectButton
                  key={connection.id}
                  connection={connection}
                  label={stalenessReconnectLabel(institutionLabel(connection))}
                  onReconnected={onReconnected}
                />
              ))}
            </div>
          )}
        </div>
      }
      actionButton={
        isNil(headerButtonConnection) ? undefined : (
          <ReconnectButton
            connection={headerButtonConnection}
            label={STALENESS_RECONNECT_LABEL}
            onReconnected={onReconnected}
          />
        )
      }
      handleClickX={onDismiss}
    />
  );
}

type AccountsStalenessBannersProps = {
  readonly connections: readonly StaleConnection[];
  readonly onDismiss: (reason: StaleConnectionReason) => void;
  readonly onReconnected: () => Promise<unknown>;
};

export function AccountsStalenessBanners({ connections, onDismiss, onReconnected }: AccountsStalenessBannersProps) {
  const disconnected = connections.filter((connection) => connection.reason === 'disconnected');
  const delayed = connections.filter((connection) => connection.reason === 'delayed-feed');

  if (connections.length === 0) {
    return null;
  }

  return (
    <div className="mb-6 flex flex-col gap-3">
      {disconnected.length > 0 && (
        <DisconnectedBanner
          connections={disconnected}
          onDismiss={() => {
            onDismiss('disconnected');
          }}
          onReconnected={onReconnected}
        />
      )}
      {delayed.length > 0 && (
        <Banner
          variant="info"
          className={CLIENT_PORTAL_BANNER_TITLE_WRAP_CLASS}
          data-testid="accounts-staleness-delayed-banner"
          title={stalenessDelayedTitle(delayed.map(institutionLabel))}
          description={stalenessDelayedDescription(institutionListLabel(delayed), oldestSyncLabel(delayed))}
          handleClickX={() => {
            onDismiss('delayed-feed');
          }}
        />
      )}
    </div>
  );
}
